/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import 'react-app-polyfill/ie11'
import 'react-app-polyfill/stable'

import React, { Suspense, createContext, useMemo } from 'react'
import { createRoot } from 'react-dom/client'
import styled from '@emotion/styled'

import PropTypes from 'prop-types'
import { BrowserRouter as Router } from 'react-router-dom'
import 'sanitize.css/sanitize.css'
import 'assets/tailwind.css'
import 'tailwindcss/base.css'
import 'tailwindcss/components.css'
import 'tailwindcss/utilities.css'

import { initializeRuntime } from 'runtimeApi'
import configureAxios from 'configureAxios'

// Import root app
import App from 'components/App'

import history from 'utils/history'

// Import Language Provider
import LanguageProvider from 'providers/LanguageProvider'

// Import Apollo provider and client
import { ApolloProvider } from '@apollo/client'
import createApolloClient from 'configureApolloClient'

import CurrentUserProvider from 'currentUserContext'
import CurrentConventionProvider, { useCurrentConvention } from 'currentConventionContext'

import { library, dom } from '@fortawesome/fontawesome-svg-core'
import('@fortawesome/free-solid-svg-icons').then(({ fas }) => library.add(fas))
import('@fortawesome/free-brands-svg-icons').then(({ fab }) => library.add(fab))
dom.watch()

import { ThemeProvider } from '@mui/material/styles'

import { createAppTheme } from 'global-styles'

import { HelmetProvider } from 'react-helmet-async'

import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'

import ThemeSwitch from 'components/ThemeSwitch'
import TestingBanner from 'components/TestingBanner'

// Import i18n messages
import { translationMessages } from './i18n'

// Create redux store with history
const MOUNT_NODE = document.getElementById('app')

if (!MOUNT_NODE) {
	throw new Error('Missing mount node: #app')
}

const app_root = createRoot(MOUNT_NODE)

const ThemeContext = createContext({})

const BuildNumber = styled.div`
	position: fixed;
	bottom: 0;
	left: 1em;
	border-top-left-radius: 0.5em;
	border-top-right-radius: 0.5em;
	background-color: rgba(0, 0, 0, 0.5);
	color: #fff;
	font-size: 0.5rem;
	padding: 0.25em 0.5em;
	z-index: 1000000;
`

// Stripe promise will be initialized after bootstrap completes
let stripePromise = null

const ThemeWrapper = ({ children }) => {
	const convention = useCurrentConvention()

	const primary_main = convention?.effective_theme?.primary_color || '#2e7d32'
	const secondary_main = convention?.effective_theme?.accent_color || '#0ea5e9'
	const mode = convention?.effective_theme?.color_mode || 'light'

	const theme = useMemo(
		() => createAppTheme({ mode, primary_main, secondary_main }),
		[mode, primary_main, secondary_main],
	)

	return <ThemeProvider theme={theme}>{children}</ThemeProvider>
}

ThemeWrapper.propTypes = {
	children: PropTypes.node,
}

const ConnectedApp = (props) => (
	<Suspense fallback={null}>
		<Router history={history}>
			<LanguageProvider messages={props.messages}>
				<ApolloProvider client={props.apollo_client}>
					<Elements stripe={stripePromise || Promise.resolve(null)}>
						<CurrentUserProvider>
							<CurrentConventionProvider>
								<HelmetProvider>
									<ThemeWrapper>
										<TestingBanner />
										<App />
										<BuildNumber>
											{process.env.ENV[0].toUpperCase()}.{process.env.BUILD_NUMBER}
										</BuildNumber>
									</ThemeWrapper>
								</HelmetProvider>
							</CurrentConventionProvider>
						</CurrentUserProvider>
					</Elements>
				</ApolloProvider>
			</LanguageProvider>
		</Router>
	</Suspense>
)

ConnectedApp.propTypes = {
	messages: PropTypes.object,
	apollo_client: PropTypes.object,
}

const render = (messages, apollo_client) => {
	app_root.render(<ConnectedApp messages={messages} apollo_client={apollo_client} />)
}

let apollo_client = null

async function start() {
	// 1) Resolve the correct API host for the current frontend domain (cookie-less bootstrap)
	const state = await initializeRuntime()

	// 2) Load Stripe publishable key from bootstrap response
	const stripe_key = state.bootstrap_response?.stripe_publishable_key
	if (stripe_key) {
		stripePromise = loadStripe(stripe_key)
	}

	// 3) Configure axios defaults to talk to the resolved API host
	configureAxios({ api_base_url: state.api_base_url, convention_host: state.convention_host })

	// 4) Build Apollo client (uses relative /graphql + axios.baseURL)
	apollo_client = createApolloClient()

	// 5) Render the app
	render(translationMessages, apollo_client)
}

if (import.meta.hot) {
	import.meta.hot.dispose(() => {
		app_root.unmount()
	})

	// Hot reloadable translation json files
	import.meta.hot.accept(['./i18n'], () => {
		if (apollo_client) render(translationMessages, apollo_client)
	})
}

start()

// if (process.env.NODE_ENV === 'production') {
// 	if ('serviceWorker' in navigator) {
// 		window.addEventListener('load', () => {
// 			navigator.serviceWorker
// 				.register('/service-worker.js')
// 				.then((registration) => {
// 					console.log('SW registered: ', registration)
// 				})
// 				.catch((registrationError) => {
// 					console.log('SW registration failed: ', registrationError)
// 				})
// 		})
// 	}
// }

// Install ServiceWorker and AppCache in the end since
// it's not most important operation and if main code fails,
// we do not want it installed
// if (process.env.NODE_ENV === 'production') {
// 	OfflinePluginRuntime.install({
// 		onUpdating: () => {
// 			console.log('SW Event:', 'onUpdating')
// 		},
// 		onUpdateReady: () => {
// 			console.log('SW Event:', 'onUpdateReady')
// 			// Tells to new SW to take control immediately
// 			OfflinePluginRuntime.applyUpdate()
// 		},
// 		onUpdated: () => {
// 			console.log('SW Event:', 'onUpdated')
// 			// Reload the webpage to load into the new version
// 			window.location.reload()
// 		},

// 		onUpdateFailed: () => {
// 			console.log('SW Event:', 'onUpdateFailed')
// 		},
// 	})
// 	// require('offline-plugin/runtime').install() // eslint-disable-line global-require
// }
